
import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion, useReducedMotion } from 'framer-motion';
import { getPopularActivities } from '@/services/activityService';
import HomeActivityCard from './HomeActivityCard';

const SkeletonCard = () => (
  <div className="bg-white rounded-xl overflow-hidden shadow-brand-sm animate-pulse">
    <div className="aspect-[16/10] bg-brand-surface-alt" />
    <div className="p-5 space-y-3">
      <div className="h-4 bg-brand-surface-alt rounded w-3/4" />
      <div className="h-3 bg-brand-surface-alt rounded w-1/2" />
      <div className="h-3 bg-brand-surface-alt rounded w-1/3" />
    </div>
  </div>
);

const PopularActivitiesSection = () => {
  const shouldReduce = useReducedMotion();

  const { data: activities = [], isLoading } = useQuery({
    queryKey: ['popular-activities'],
    queryFn: () => getPopularActivities(6),
    staleTime: 5 * 60 * 1000,
  });

  return (
    <section className="max-w-[1280px] mx-auto px-4 sm:px-6 pt-10 sm:pt-20">
      {/* Section header */}
      <div className="flex items-end justify-between gap-6 mb-8">
        <div>
          <h2 className="font-heading font-extrabold text-brand-dark tracking-tight leading-tight" style={{ fontSize: 'clamp(28px, 3vw, 40px)', letterSpacing: '-0.02em' }}>
            Les <em className="not-italic text-brand-primary">chouchous</em> du moment
          </h2>
          <p className="text-brand-text-muted mt-2 text-base">Les activités préférées des groupes ce mois-ci.</p>
        </div>
        <Link to="/search" className="text-brand-text font-semibold text-sm hover:text-brand-primary transition-colors flex-shrink-0">
          Voir tout →
        </Link>
      </div>

      {/* Mobile: horizontal swipe scroll. sm+: grid */}
      <div className="relative">
        <div
          className="flex overflow-x-auto snap-x snap-mandatory gap-5 -mx-4 px-4 pb-3 scrollbar-none sm:grid sm:grid-cols-2 lg:grid-cols-3 sm:overflow-visible sm:mx-0 sm:px-0 sm:pb-0"
        >
          {isLoading
            ? Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="snap-start shrink-0 w-[80vw] max-w-[300px] sm:w-auto sm:max-w-none">
                  <SkeletonCard />
                </div>
              ))
            : activities.map((activity, i) => (
                <motion.div
                  key={activity.id}
                  initial={shouldReduce ? false : { opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.4, delay: i * 0.06, ease: [0.25, 0.46, 0.45, 0.94] }}
                  className="snap-start shrink-0 w-[80vw] max-w-[300px] sm:w-auto sm:max-w-none"
                >
                  <HomeActivityCard activity={activity} />
                </motion.div>
              ))}
        </div>
        {/* Right-edge fade hint — mobile only */}
        <div className="sm:hidden pointer-events-none absolute top-0 right-0 w-10 h-full bg-gradient-to-l from-brand-bg to-transparent" />
      </div>
    </section>
  );
};

export default PopularActivitiesSection;
